import DOMPurify from 'dompurify';

/**
 * Cleans the CAT graph picture before the app shows it (Issue #2399).
 *
 * The picture is an SVG document drawn by the `cat-graph` service (#2538) from the stored graph.
 * The app holds no layout and no drawing code of its own: what it shows is the service's picture.
 * The picture arrives as text, so it goes through the frontend's rule for such markup from #1773
 * (`shared/citations/citation-markdown.ts`): clean it with DOMPurify against a closed list of
 * allowed elements and attributes, then trust the cleaned document as a whole.
 *
 * The lists are what the renderer writes and nothing more. Note the absences:
 *
 * - No `a`. A chip whose evidence is a citation names its citations (`data-citations`) and the app
 *   opens them, so a click stays in the app and in the same environment. The renderer still writes
 *   an `a` for a document outside the app; removing it leaves the chip visible as text.
 * - No `style`. The renderer writes its look as attributes (#2538), and a `<style>` element is the
 *   one element on such a list that can load content from another site.
 * - No `href`, no `on*` handler, no `class`. The picture cannot address the app's stylesheet.
 */

/** Elements the picture may contain. */
const ALLOWED_TAGS: readonly string[] = Object.freeze(['svg', 'g', 'rect', 'path', 'text', 'title']);

/** Attributes the picture may carry: geometry, look, the identifiers the app reads, and accessibility. */
const ALLOWED_ATTR: readonly string[] = Object.freeze([
  'xmlns',
  'width',
  'height',
  // DOMPurify compares attribute names in lower case, so `viewBox` is listed as it compares it.
  'viewbox',
  'x',
  'y',
  'rx',
  'd',
  'fill',
  'stroke',
  'stroke-width',
  'stroke-dasharray',
  'font-family',
  'font-size',
  'font-style',
  'font-weight',
  'letter-spacing',
  'text-anchor',
  'text-decoration',
  'role',
  'tabindex',
  'aria-label',
  'data-argument',
  'data-node',
  'data-evidence',
  'data-citations',
  'data-wires',
]);

/** A citation ID as the product message allows it, and as the mapper accepts it. */
const CITATION_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

/** The picture, cleaned, with what the view needs to know about it. */
export interface CleanCatGraphPicture {
  /** The cleaned SVG document, serialized. Safe to trust as a whole. */
  svg: string;
  /** The picture's own width in pixels, from its `viewBox`. Zero when it has none. */
  width: number;
  /** The picture's own height in pixels, from its `viewBox`. Zero when it has none. */
  height: number;
}

/**
 * Cleans a picture from the service. Returns `null` when the text holds no SVG document at all,
 * so the view can say the picture could not be shown and fall back to the list of claims.
 *
 * Besides cleaning, two attributes of the root element change, neither of which affects what is
 * drawn: its fixed `width` and `height` are removed so the view can scale the picture, and its role
 * becomes `group`, because a screen reader treats everything inside `role="img"` as one image and
 * would never reach the evidence chips, which are links. Each chip that names citations becomes
 * `role="link"` with `tabindex="0"`, so the picture cannot change the page's focus order or present
 * a chip as something else.
 */
export function cleanCatGraphPicture(markup: string): CleanCatGraphPicture | null {
  const fragment = DOMPurify.sanitize(markup, {
    ALLOWED_TAGS: [...ALLOWED_TAGS],
    ALLOWED_ATTR: [...ALLOWED_ATTR],
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
    RETURN_DOM_FRAGMENT: true,
  });
  const svg = Array.from(fragment.childNodes).find(
    (node): node is SVGSVGElement => node instanceof Element && node.localName === 'svg'
  );
  if (!svg) return null;

  const [, , width, height] = (svg.getAttribute('viewBox') ?? '').split(/[\s,]+/).map(Number);
  svg.removeAttribute('width');
  svg.removeAttribute('height');
  svg.setAttribute('role', 'group');
  for (const chip of Array.from(svg.querySelectorAll('[data-citations]'))) {
    // Only a chip that names citations is a link. Anything else that claims to be one is not.
    if (chipCitationIds(chip).length === 0) {
      chip.removeAttribute('data-citations');
    } else {
      // A chip is a link in the page's own focus order, whatever role or order the picture gave it.
      chip.setAttribute('role', 'link');
      chip.setAttribute('tabindex', '0');
    }
  }
  for (const element of Array.from(svg.querySelectorAll('[role="link"], [tabindex]'))) {
    if (!element.hasAttribute('data-citations')) {
      element.removeAttribute('role');
      element.removeAttribute('tabindex');
    }
  }
  return {
    svg: new XMLSerializer().serializeToString(svg),
    width: Number.isFinite(width) && width > 0 ? width : 0,
    height: Number.isFinite(height) && height > 0 ? height : 0,
  };
}

/** The citation IDs a chip names, in its order. IDs that the product message does not allow are dropped. */
export function chipCitationIds(chip: Element): string[] {
  return (chip.getAttribute('data-citations') ?? '').split(/\s+/).filter(id => CITATION_ID_PATTERN.test(id));
}

/** Where in the graph a chip or box of the picture sits, read from its identifiers. */
export interface PictureTarget {
  /** A chip that names citations: the chip element and its citation IDs. */
  chip?: { element: Element; citationIds: string[] };
  /** The box the event happened in, by argument and node ID. */
  box?: { argumentId: string; nodeId: string };
}

/**
 * Finds what an event in the picture was aimed at, by walking up from its target. Returns an empty
 * object when the event was outside every box, or outside the picture.
 */
export function pictureTarget(target: EventTarget | null, picture: Element): PictureTarget {
  if (!(target instanceof Element) || !picture.contains(target)) return {};
  const result: PictureTarget = {};
  const chip = target.closest('[data-citations]');
  if (chip && picture.contains(chip)) {
    const citationIds = chipCitationIds(chip);
    if (citationIds.length > 0) result.chip = { element: chip, citationIds };
  }
  const box = target.closest('[data-node]');
  if (box && picture.contains(box)) {
    result.box = { argumentId: box.getAttribute('data-argument') ?? '', nodeId: box.getAttribute('data-node') ?? '' };
  }
  return result;
}
